import * as THREE from 'three';
import { state, notify, type Fps, type TimeUnit } from '../state.ts';
import type { RenderLoop } from '../scene/renderLoop.ts';

/**
 * Timeline controller: owns frame<->time mapping, FPS selection, scrubbing and
 * the marked [start, end] segment. Scrubbing pauses playback and drives the
 * mixer synchronously via setTime so updates are frame-accurate (SPEC §3.4).
 */
export interface Timeline {
  setupClip: (clip: THREE.AnimationClip, mixers: THREE.AnimationMixer[]) => void;
  reset: () => void;
  setFps: (fps: Fps) => void;
  togglePlay: () => void;
  play: () => void;
  pause: () => void;
  scrubToFrame: (frame: number) => void;
  stepFrame: (delta: number) => void;
  jumpToStart: () => void;
  jumpToEnd: () => void;
  markStart: (frame?: number) => void;
  markEnd: (frame?: number) => void;
  setTimeUnit: (unit: TimeUnit) => void;
}

export function createTimeline(loop: RenderLoop): Timeline {
  function recomputeTotalFrames(): void {
    if (!state.clip) {
      state.totalFrames = 0;
      return;
    }
    state.totalFrames = Math.max(1, Math.round(state.clip.duration * state.fps));
  }

  function clampFrame(frame: number): number {
    return Math.min(Math.max(0, Math.round(frame)), state.totalFrames);
  }

  /** Drive every actor mixer to the same time so they stay frame-synced. */
  function setAllTime(seconds: number): void {
    for (const m of state.mixers) m.setTime(seconds);
  }

  function setupClip(clip: THREE.AnimationClip, mixers: THREE.AnimationMixer[]): void {
    state.clip = clip;
    state.mixers = mixers;
    state.mixer = mixers[0] ?? null;
    recomputeTotalFrames();
    state.currentFrame = 0;
    state.startFrame = 0;
    state.endFrame = state.totalFrames;
    setAllTime(0);
    notify();
  }

  function reset(): void {
    state.clip = null;
    state.mixer = null;
    state.mixers = [];
    state.playing = false;
    state.totalFrames = 0;
    state.currentFrame = 0;
    state.startFrame = 0;
    state.endFrame = 0;
    notify();
  }

  function setFps(fps: Fps): void {
    // Preserve the playhead in seconds across an fps change.
    const seconds = state.mixer ? state.mixer.time : 0;
    state.fps = fps;
    recomputeTotalFrames();
    state.currentFrame = clampFrame(seconds * fps);
    state.startFrame = clampFrame(state.startFrame);
    state.endFrame = state.endFrame === 0 ? state.totalFrames : clampFrame(state.endFrame);
    if (state.endFrame <= state.startFrame) state.endFrame = state.totalFrames;
    notify();
  }

  function play(): void {
    if (!state.mixer || state.totalFrames === 0) return;
    // Resume from the start of the marked range if we're parked at/after the end.
    if (state.currentFrame >= state.endFrame) {
      setAllTime(state.startFrame / state.fps);
      state.currentFrame = state.startFrame;
    }
    state.playing = true;
    notify();
  }

  function pause(): void {
    state.playing = false;
    notify();
  }

  function togglePlay(): void {
    if (state.playing) pause();
    else play();
  }

  function scrubToFrame(frame: number): void {
    if (!state.mixer) return;
    state.playing = false;
    state.currentFrame = clampFrame(frame);
    // Force a synchronous animation update at the exact frame time on all actors.
    setAllTime(state.currentFrame / state.fps);
    loop.renderOnce();
    notify();
  }

  /** Step the playhead by ±N frames (pauses playback), Motive-style. */
  function stepFrame(delta: number): void {
    scrubToFrame(state.currentFrame + delta);
  }

  function jumpToStart(): void {
    scrubToFrame(state.startFrame);
  }

  function jumpToEnd(): void {
    scrubToFrame(state.endFrame);
  }

  function setTimeUnit(unit: TimeUnit): void {
    state.timeUnit = unit;
    notify();
  }

  function markStart(frame = state.currentFrame): void {
    state.startFrame = clampFrame(frame);
    if (state.endFrame <= state.startFrame) {
      state.endFrame = Math.min(state.totalFrames, state.startFrame + 1);
    }
    notify();
  }

  function markEnd(frame = state.currentFrame): void {
    state.endFrame = clampFrame(frame);
    if (state.endFrame <= state.startFrame) {
      state.startFrame = Math.max(0, state.endFrame - 1);
    }
    notify();
  }

  return {
    setupClip,
    reset,
    setFps,
    togglePlay,
    play,
    pause,
    scrubToFrame,
    stepFrame,
    jumpToStart,
    jumpToEnd,
    markStart,
    markEnd,
    setTimeUnit,
  };
}
